/**
 * useFocusTrap Hook
 *
 * Traps focus within a container element when active.
 * Used by StackedDrawer to keep keyboard focus within the drawer.
 *
 * Features:
 * - Tab cycles through focusable elements
 * - Shift+Tab cycles backwards
 * - Focus wraps from last to first and vice versa
 *
 * @see docs/stories/DRAWER-4.3-implement-keyboard-support.md
 */

import { useEffect, useRef } from "react";

/** Selector for focusable elements */
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface UseFocusTrapOptions {
  /** Whether focus trap is active */
  isActive: boolean;
  /** Whether to auto-focus first element when trap activates */
  autoFocus?: boolean;
  /** Whether to return focus when trap deactivates */
  returnFocus?: boolean;
}

/**
 * Traps focus within a container element.
 *
 * @param containerRef - Ref to the container element
 * @param options - Focus trap options
 *
 * @example
 * ```tsx
 * function Modal({ isOpen, children }) {
 *   const modalRef = useRef<HTMLDivElement>(null);
 *   useFocusTrap(modalRef, { isActive: isOpen });
 *
 *   return <div ref={modalRef}>{children}</div>;
 * }
 * ```
 */
export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  options: UseFocusTrapOptions
) {
  const { isActive, autoFocus = true, returnFocus = true } = options;
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Store and restore focus
  useEffect(() => {
    if (isActive) {
      // Store current focus
      previousActiveElement.current = document.activeElement as HTMLElement;

      // Auto-focus first focusable element
      if (autoFocus && containerRef.current) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          const firstFocusable = containerRef.current?.querySelector(
            FOCUSABLE_SELECTOR
          ) as HTMLElement | null;
          firstFocusable?.focus();
        });
      }
    } else if (returnFocus && previousActiveElement.current) {
      // Restore focus when deactivating
      previousActiveElement.current.focus();
    }
  }, [isActive, autoFocus, returnFocus, containerRef]);

  // Handle Tab key for focus cycling
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const focusableElements = container.querySelectorAll(FOCUSABLE_SELECTOR);
      const focusableArray = Array.from(focusableElements) as HTMLElement[];

      if (focusableArray.length === 0) return;

      const firstElement = focusableArray[0];
      const lastElement = focusableArray[focusableArray.length - 1];

      if (e.shiftKey) {
        // Shift + Tab: wrap from first to last
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: wrap from last to first
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    container.addEventListener("keydown", handleKeyDown);
    return () => container.removeEventListener("keydown", handleKeyDown);
  }, [isActive, containerRef]);
}

export default useFocusTrap;
